import { useEffect, useState } from "react";
import { toast } from "sonner";

import legalAdviceHeadset from "assets/svg/legal-advice-headset.svg";
import {
  getOppositionHearing,
  getOppositionProgress,
  recordOppositionFilingOfHearingDocument,
  OppositionWorkflowStep,
  type OppositionHearingItem,
  type OppositionProgressStep,
} from "api/opposition";

export const OPPOSITION_FILING_OF_HEARING_DOCUMENT_FORM_ID =
  "opposition-filing-of-hearing-document-form";

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionFilingOfHearingDocumentView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [marks, setMarks] = useState<OppositionHearingItem[]>([]);
  const [recording, setRecording] = useState(false);
  const [recorded, setRecorded] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getOppositionHearing(leadId),
      getOppositionProgress(leadId).catch(() => null),
    ])
      .then(([data, progress]) => {
        if (cancelled) return;
        setMarks(
          data.conflictingMarks.filter((m) => m.dateOfOppositionHearing != null)
        );
        if (progress) {
          const step = progress.steps.find(
            (s: OppositionProgressStep) =>
              s.slug ===
              OppositionWorkflowStep.OP_S3_38_FilingOfOppositionHearingDocument
          );
          setRecorded(Boolean(step?.isCompleted));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load step data"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || recording) return;
    if (recorded) {
      onSuccess?.(leadId);
      return;
    }
    if (marks.length === 0) {
      toast.error("No eligible applications.");
      return;
    }
    setRecording(true);
    try {
      await recordOppositionFilingOfHearingDocument(leadId, {
        conflictingMarkIds: marks.map((m) => m.id),
      });
      setRecorded(true);
      toast.success("Step recorded.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to record step");
    } finally {
      setRecording(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_FILING_OF_HEARING_DOCUMENT_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex w-full max-w-[1085px] flex-col items-center justify-center gap-3 py-10"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <img
        src={legalAdviceHeadset}
        alt=""
        className="w-[120px] h-[120px] opacity-80"
      />
      <p className="text-base font-semibold text-[#222]">
        Filing of Opposition Hearing Document
      </p>
      <p className="max-w-[520px] text-center text-sm text-[#6D6D6D]">
        File the prepared hearing documents at the Trademarks Registry before
        the Opposition Hearing date.
      </p>
      {recorded && (
        <span className="text-xs font-semibold text-[#222]">
          Step already recorded.
        </span>
      )}
    </form>
  );
}
